import React from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';

// Constants for supported entry points
const ENTRY_POINTS = {
  DASHBOARD: 'dashboard',
  USERPORTAL: 'userportal',
};

/**
 * Load the appropriate entry point based on the environment variable
 * Format of REACT_APP_ENTRY_POINT can be:
 * - 'dashboard' or 'dashboard/index.tsx' (loads from /dashboard folder)
 * - 'userportal' or 'userportal/index.tsx' (loads from /userportal folder)
 * - 'App' or undefined (loads default App)
 */
const loadEntryPoint = async () => {
  // Parse the entry point from environment variable
  const rawEntryPoint = process.env.REACT_APP_ENTRY_POINT || 'dashboard';

  // Remove file extension and normalize
  const cleanEntryPoint = rawEntryPoint.replace(/\.tsx$/, '');

  try {
    // Determine which entry point to load
    let EntryPointComponent;

    if (
      cleanEntryPoint.toLowerCase() === ENTRY_POINTS.DASHBOARD ||
      cleanEntryPoint.toLowerCase() === 'app' ||
      !cleanEntryPoint
    ) {
      // Load dashboard entry point
      const { default: Dashboard } = await import('./dashboard');
      EntryPointComponent = Dashboard;
    } else if (cleanEntryPoint.toLowerCase() === ENTRY_POINTS.USERPORTAL) {
      // Load userportal entry point
      const { default: UserPortal } = await import('./userportal');
      EntryPointComponent = UserPortal;
    } else {
      // Try to dynamically load any other entry point
      try {
        const { default: CustomEntryPoint } = await import(`./${cleanEntryPoint}`);
        EntryPointComponent = CustomEntryPoint;
      } catch {
        throw new Error(`Unsupported entry point: ${cleanEntryPoint}`);
      }
    }

    // Render the loaded entry point
    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
    root.render(
      <React.StrictMode>
        <EntryPointComponent />
      </React.StrictMode>
    );

    // Load reportWebVitals
    reportWebVitals();
  } catch (err) {
    // Fallback to an error screen
    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
    root.render(
      <React.StrictMode>
        <div>
          <h1>Error Loading Application</h1>
          <p>
            Failed to load entry point: <strong>{rawEntryPoint}</strong>
          </p>
          <p>{err instanceof Error ? err.message : String(err)}</p>
          <p>Please check your configuration and try again.</p>
        </div>
      </React.StrictMode>
    );
  }
};

// Start the application by loading the appropriate entry point
loadEntryPoint();
